import type { TranslationMessages } from "ra-core";

/**
 * Spanish translation of the react-admin core messages. Kept in-repo because the
 * published ra-language-spanish (1.0.0) predates this react-admin major and is
 * missing most of the keys below.
 */
export const spanishRaMessages: TranslationMessages = {
  ra: {
    action: {
      add_filter: "Añadir filtro",
      add: "Añadir",
      back: "Volver",
      bulk_actions:
        "1 elemento seleccionado |||| %{smart_count} elementos seleccionados",
      cancel: "Cancelar",
      clear_array_input: "Vaciar la lista",
      clear_input_value: "Borrar el valor",
      clone: "Duplicar",
      confirm: "Confirmar",
      create: "Crear",
      create_item: "Crear %{item}",
      delete: "Eliminar",
      edit: "Editar",
      export: "Exportar",
      list: "Lista",
      refresh: "Actualizar",
      remove_filter: "Quitar este filtro",
      remove_all_filters: "Quitar todos los filtros",
      remove: "Quitar",
      reset: "Restablecer",
      save: "Guardar",
      search: "Buscar",
      search_columns: "Buscar columnas",
      select_all: "Seleccionar todo",
      select_all_button: "Seleccionar todo",
      select_row: "Seleccionar esta fila",
      show: "Ver",
      sort: "Ordenar",
      undo: "Deshacer",
      unselect: "Deseleccionar",
      expand: "Desplegar",
      close: "Cerrar",
      open_menu: "Abrir el menú",
      close_menu: "Cerrar el menú",
      update: "Actualizar",
      move_up: "Subir",
      move_down: "Bajar",
      open: "Abrir",
      toggle_theme: "Cambiar entre modo claro y oscuro",
      select_columns: "Columnas",
      update_application: "Recargar la aplicación",
    },
    boolean: {
      true: "Sí",
      false: "No",
      null: " ",
    },
    page: {
      create: "Crear %{name}",
      dashboard: "Panel",
      edit: "%{name} %{recordRepresentation}",
      error: "Se ha producido un error",
      list: "%{name}",
      loading: "Cargando",
      not_found: "No encontrado",
      show: "%{name} %{recordRepresentation}",
      empty: "Todavía no hay %{name}.",
      invite: "¿Quiere crear el primer registro?",
      access_denied: "Acceso denegado",
      authentication_error: "Error de autenticación",
    },
    input: {
      file: {
        upload_several:
          "Suelte aquí los archivos que quiera subir o haga clic para seleccionarlos.",
        upload_single:
          "Suelte aquí el archivo que quiera subir o haga clic para seleccionarlo.",
      },
      image: {
        upload_several:
          "Suelte aquí las imágenes que quiera subir o haga clic para seleccionarlas.",
        upload_single:
          "Suelte aquí la imagen que quiera subir o haga clic para seleccionarla.",
      },
      references: {
        all_missing: "No se han encontrado los datos de las referencias.",
        many_missing:
          "Al menos una de las referencias asociadas ya no está disponible.",
        single_missing: "La referencia asociada ya no está disponible.",
      },
      password: {
        toggle_visible: "Ocultar la contraseña",
        toggle_hidden: "Mostrar la contraseña",
      },
    },
    message: {
      about: "Acerca de",
      access_denied: "No tiene permisos para acceder a esta página",
      are_you_sure: "¿Está seguro?",
      authentication_error:
        "El servidor de autenticación ha devuelto un error y no se han podido verificar sus credenciales.",
      auth_error:
        "Se ha producido un error al validar el token de autenticación.",
      bulk_delete_content:
        "¿Seguro que quiere eliminar este elemento? |||| ¿Seguro que quiere eliminar estos %{smart_count} elementos?",
      bulk_delete_title:
        "Eliminar %{name} |||| Eliminar %{smart_count} %{name}",
      bulk_update_content:
        "¿Seguro que quiere actualizar %{name} %{recordRepresentation}? |||| ¿Seguro que quiere actualizar estos %{smart_count} elementos?",
      bulk_update_title:
        "Actualizar %{name} %{recordRepresentation} |||| Actualizar %{smart_count} %{name}",
      clear_array_input: "¿Seguro que quiere vaciar la lista completa?",
      delete_content: "¿Seguro que quiere eliminar este elemento?",
      delete_title: "Eliminar %{name} %{recordRepresentation}",
      details: "Detalles",
      error:
        "Se ha producido un error en la aplicación y no se ha podido completar su solicitud.",
      invalid_form: "El formulario no es válido. Revise los errores",
      loading: "Espere, por favor",
      no: "No",
      not_found:
        "Ha escrito una URL incorrecta o ha seguido un enlace erróneo.",
      select_all_limit_reached:
        "Hay demasiados elementos para seleccionarlos todos. Solo se han seleccionado los primeros %{max}.",
      unsaved_changes:
        "Algunos de sus cambios no se han guardado. ¿Seguro que quiere descartarlos?",
      yes: "Sí",
      placeholder_data_warning:
        "Problema de red: no se han podido actualizar los datos.",
    },
    navigation: {
      clear_filters: "Quitar los filtros",
      no_filtered_results:
        "No se han encontrado %{name} con los filtros actuales.",
      no_results: "No se han encontrado %{name}",
      no_more_results:
        "La página %{page} está fuera de los límites. Pruebe con la página anterior.",
      page_out_of_boundaries: "La página %{page} está fuera de los límites",
      page_out_from_end: "No se puede avanzar más allá de la última página",
      page_out_from_begin: "No se puede retroceder más allá de la página 1",
      page_range_info: "%{offsetBegin}-%{offsetEnd} de %{total}",
      partial_page_range_info:
        "%{offsetBegin}-%{offsetEnd} de más de %{offsetEnd}",
      current_page: "Página %{page}",
      page: "Ir a la página %{page}",
      first: "Ir a la primera página",
      last: "Ir a la última página",
      next: "Ir a la página siguiente",
      previous: "Ir a la página anterior",
      page_rows_per_page: "Filas por página:",
      skip_nav: "Saltar al contenido",
    },
    sort: {
      sort_by: "Ordenar por %{field_lower_first} %{order}",
      ASC: "ascendente",
      DESC: "descendente",
    },
    auth: {
      auth_check_error: "Inicie sesión para continuar",
      user_menu: "Perfil",
      username: "Usuario",
      password: "Contraseña",
      email: "Correo electrónico",
      sign_in: "Iniciar sesión",
      sign_in_error: "No se ha podido iniciar sesión, inténtelo de nuevo",
      logout: "Cerrar sesión",
    },
    notification: {
      updated:
        "Elemento actualizado |||| %{smart_count} elementos actualizados",
      created: "Elemento creado",
      deleted: "Elemento eliminado |||| %{smart_count} elementos eliminados",
      bad_item: "Elemento incorrecto",
      item_doesnt_exist: "El elemento no existe",
      http_error: "Error de comunicación con el servidor",
      data_provider_error:
        "Error del dataProvider. Consulte la consola para más detalles.",
      i18n_error:
        "No se han podido cargar las traducciones del idioma seleccionado",
      canceled: "Acción cancelada",
      logged_out: "Su sesión ha finalizado, vuelva a conectarse.",
      not_authorized: "No tiene autorización para acceder a este recurso.",
      application_update_available: "Hay una versión nueva disponible.",
      offline: "Sin conexión. No se han podido obtener los datos.",
    },
    validation: {
      required: "Obligatorio",
      minLength: "Debe tener como mínimo %{min} caracteres",
      maxLength: "Debe tener como máximo %{max} caracteres",
      minValue: "Debe ser como mínimo %{min}",
      maxValue: "Debe ser %{max} o menos",
      number: "Debe ser un número",
      email: "Debe ser un correo electrónico válido",
      oneOf: "Debe ser uno de estos valores: %{options}",
      regex:
        "Debe coincidir con un formato concreto (expresión regular): %{pattern}",
      unique: "Debe ser único",
    },
    saved_queries: {
      label: "Consultas guardadas",
      query_name: "Nombre de la consulta",
      new_label: "Guardar la consulta actual...",
      new_dialog_title: "Guardar la consulta actual como",
      remove_label: "Eliminar la consulta guardada",
      remove_label_with_name: 'Eliminar la consulta "%{name}"',
      remove_dialog_title: "¿Eliminar la consulta guardada?",
      remove_message:
        "¿Seguro que quiere eliminar ese elemento de su lista de consultas guardadas?",
      help: "Filtre la lista y guarde esta consulta para más adelante",
    },
    guesser: {
      empty: {
        title: "No hay datos que mostrar",
        message: "Compruebe su data provider",
      },
    },
    configurable: {
      customize: "Personalizar",
      configureMode: "Configurar esta página",
      inspector: {
        title: "Inspector",
        content:
          "Pase el cursor sobre los elementos de la interfaz para configurarlos",
        reset: "Restablecer la configuración",
        hideAll: "Ocultar todo",
        showAll: "Mostrar todo",
      },
      Datagrid: {
        title: "Tabla de datos",
        unlabeled: "Columna sin etiqueta n.º %{column}",
      },
      SimpleForm: {
        title: "Formulario",
        unlabeled: "Campo sin etiqueta n.º %{input}",
      },
      SimpleList: {
        title: "Lista",
        primaryText: "Texto principal",
        secondaryText: "Texto secundario",
        tertiaryText: "Texto terciario",
      },
    },
  },
};
